// QA Template builder — office/admin only. Lets the customer create
// custom QA forms for civil tasks we haven't shipped (e.g. "Board and
// batten fence install", "Storage tank commissioning"). Uses the
// existing /qa/templates CRUD backend.
import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Trash2, Save, Copy, X, GripVertical, PenSquare, ListChecks, Ruler, Lock, Upload, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const FIELD_TYPE_LABEL = { text: "Text", number: "Number", select: "Choice" };

// Slugify a label to a stable field key. If two fields collide we
// append a numeric suffix so the record spec_values map stays unique.
const toKey = (label, existingKeys) => {
  const base = (label || "field")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "field";
  let key = base;
  let n = 2;
  while (existingKeys.has(key)) {
    key = `${base}_${n}`;
    n += 1;
  }
  return key;
};

export function QATemplateManagerDialog({ open, onOpenChange, onChanged }) {
  const [meta, setMeta] = useState({ task_types: [], task_labels: {} });
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef(null);

  const importForm = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/qa/templates/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setTemplates((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      setEditing(data);
      toast.success(`"${data.name}" imported — give it a once-over and save`);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed — try a PDF or a clear photo of the form");
    } finally {
      setImporting(false);
      if (importInputRef.current) importInputRef.current.value = "";
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [m, t] = await Promise.all([
        api.get("/qa/meta"),
        api.get("/qa/templates"),
      ]);
      setMeta(m.data || { task_types: [], task_labels: {} });
      setTemplates(Array.isArray(t.data) ? t.data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const startNew = () => setEditing({
    id: null,
    name: "",
    task_type: "other",
    photos_required: false,
    spec_fields: [],
    checklist: [],
    system: false,
  });

  const startEdit = (tpl) => setEditing({
    id: tpl.id,
    name: tpl.name,
    task_type: tpl.task_type || "other",
    photos_required: !!tpl.photos_required,
    spec_fields: (tpl.spec_fields || []).map((f) => ({ ...f })),
    checklist: [...(tpl.checklist || [])],
    system: !!tpl.system,
  });

  const clone = async (tpl) => {
    try {
      const { data } = await api.post(`/qa/templates/${tpl.id}/clone`);
      toast.success(`Cloned as "${data.name}"`);
      setTemplates((prev) => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clone failed");
    }
  };

  const remove = async (tpl) => {
    if (tpl.system) {
      toast.error("System templates can't be deleted — clone instead");
      return;
    }
    if (!window.confirm(`Delete "${tpl.name}"?\n\nExisting QA records that used this template stay intact — you just can't start new ones from it.`)) return;
    try {
      await api.delete(`/qa/templates/${tpl.id}`);
      setTemplates((prev) => prev.filter((t) => t.id !== tpl.id));
      toast.success("Template deleted");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0" data-testid="qa-templates-dialog">
        <DialogHeader className="p-5 pb-3 border-b border-zinc-200">
          <DialogTitle className="font-display font-black uppercase tracking-tight text-lg">
            QA templates
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Fieldlink ships a few defaults. Add your own for anything we haven't covered — set up the spec fields, checklist and photo requirement once, then your crews just pick it and fill it in.
          </DialogDescription>
        </DialogHeader>

        {editing ? (
          <TemplateEditor
            meta={meta}
            initial={editing}
            onCancel={() => setEditing(null)}
            onSaved={(saved) => {
              setTemplates((prev) => {
                const others = prev.filter((t) => t.id !== saved.id);
                return [...others, saved].sort((a, b) => a.name.localeCompare(b.name));
              });
              setEditing(null);
              onChanged?.();
            }}
          />
        ) : (
          <div className="p-5 space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                {templates.length} template{templates.length === 1 ? "" : "s"} · {templates.filter((t) => t.system).length} shipped by Fieldlink · {templates.filter((t) => !t.system).length} custom
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => importInputRef.current?.click()}
                  disabled={importing}
                  className="rounded-sm border-zinc-400 uppercase tracking-wider font-bold text-xs"
                  data-testid="qa-tpl-import"
                >
                  {importing ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Reading your form…</>
                  ) : (
                    <><Upload className="w-3.5 h-3.5 mr-1.5" /> Import a form</>
                  )}
                </Button>
                <input
                  ref={importInputRef}
                  type="file"
                  accept=".pdf,.docx,.xlsx,.xlsm,.png,.jpg,.jpeg,.webp,.txt,.csv,.md"
                  className="hidden"
                  onChange={importForm}
                  data-testid="qa-tpl-import-input"
                />
                <Button
                  onClick={startNew}
                  className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs"
                  data-testid="qa-tpl-new"
                >
                  <Plus className="w-3.5 h-3.5 mr-1.5" /> New template
                </Button>
              </div>
            </div>
            {importing && (
              <div className="border-l-4 border-[#FF5F15] bg-orange-50 p-3 text-xs text-zinc-700" data-testid="qa-tpl-import-progress">
                Converting your form into a Fieldlink template with AI — usually takes 10–20 seconds. Hang tight.
              </div>
            )}

            {loading ? (
              <div className="space-y-2">{[1, 2, 3].map((i) => (<div key={i} className="h-14 bg-zinc-100 animate-pulse border border-zinc-200" />))}</div>
            ) : templates.length === 0 ? (
              <div className="border-2 border-dashed border-zinc-300 p-10 text-center">
                <ListChecks className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                <div className="font-bold uppercase tracking-tight">No templates yet</div>
                <p className="text-xs text-zinc-500 mt-1">Add one to get started.</p>
              </div>
            ) : (
              <div className="border border-zinc-200 divide-y divide-zinc-100">
                {templates.map((t) => (
                  <div key={t.id} className="p-3 flex items-center gap-3 hover:bg-zinc-50" data-testid={`qa-tpl-row-${t.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-sm truncate">{t.name}</span>
                        {t.system ? (
                          <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest border border-zinc-300 text-zinc-600 px-1.5 py-0.5">
                            <Lock className="w-2.5 h-2.5" /> Shipped
                          </span>
                        ) : (
                          <span className="text-[9px] font-bold uppercase tracking-widest bg-[#FF5F15] text-white px-1.5 py-0.5">Custom</span>
                        )}
                        <span className="text-[10px] font-mono text-zinc-500">
                          {meta.task_labels[t.task_type] || t.task_type}
                        </span>
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-0.5 truncate">
                        {(t.spec_fields || []).length} spec field{(t.spec_fields || []).length === 1 ? "" : "s"} · {(t.checklist || []).length} checklist item{(t.checklist || []).length === 1 ? "" : "s"}
                        {t.photos_required && <> · <span className="text-[#FF5F15] font-bold">Photos required</span></>}
                      </div>
                    </div>
                    <button onClick={() => startEdit(t)} className="p-1.5 text-zinc-600 hover:text-[#FF5F15]" title={t.system ? "View (clone to edit)" : "Edit"} data-testid={`qa-tpl-edit-${t.id}`}>
                      <PenSquare className="w-4 h-4" />
                    </button>
                    <button onClick={() => clone(t)} className="p-1.5 text-zinc-600 hover:text-blue-600" title="Clone" data-testid={`qa-tpl-clone-${t.id}`}>
                      <Copy className="w-4 h-4" />
                    </button>
                    {!t.system && (
                      <button onClick={() => remove(t)} className="p-1.5 text-zinc-400 hover:text-red-600" title="Delete" data-testid={`qa-tpl-del-${t.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function TemplateEditor({ meta, initial, onCancel, onSaved }) {
  const [tpl, setTpl] = useState(initial);
  const [saving, setSaving] = useState(false);
  const isSystem = !!initial.system && !!initial.id;
  // Shipped templates are read-only — office must clone before editing.
  // We render everything disabled and hide the Save button.

  const existingKeys = useMemo(
    () => new Set((tpl.spec_fields || []).map((f) => f.key).filter(Boolean)),
    [tpl.spec_fields],
  );

  const addSpec = () => setTpl((t) => ({
    ...t,
    spec_fields: [...t.spec_fields, { key: "", label: "", type: "text", options: null, suffix: null }],
  }));
  const updSpec = (i, patch) => setTpl((t) => ({
    ...t,
    spec_fields: t.spec_fields.map((f, idx) => (idx === i ? { ...f, ...patch } : f)),
  }));
  const rmSpec = (i) => setTpl((t) => ({
    ...t,
    spec_fields: t.spec_fields.filter((_, idx) => idx !== i),
  }));
  const moveSpec = (i, dir) => setTpl((t) => {
    const j = i + dir;
    if (j < 0 || j >= t.spec_fields.length) return t;
    const next = [...t.spec_fields];
    [next[i], next[j]] = [next[j], next[i]];
    return { ...t, spec_fields: next };
  });

  const addChecklist = () => setTpl((t) => ({ ...t, checklist: [...t.checklist, ""] }));
  const updChecklist = (i, v) => setTpl((t) => ({
    ...t,
    checklist: t.checklist.map((c, idx) => (idx === i ? v : c)),
  }));
  const rmChecklist = (i) => setTpl((t) => ({
    ...t,
    checklist: t.checklist.filter((_, idx) => idx !== i),
  }));
  const moveChecklist = (i, dir) => setTpl((t) => {
    const j = i + dir;
    if (j < 0 || j >= t.checklist.length) return t;
    const next = [...t.checklist];
    [next[i], next[j]] = [next[j], next[i]];
    return { ...t, checklist: next };
  });

  const save = async () => {
    if (!tpl.name.trim()) return toast.error("Give the template a name");
    if (!meta.task_types.includes(tpl.task_type)) return toast.error("Pick a task type");
    // Sanitise & auto-key any fields that have a label but no key
    const seen = new Set();
    const spec_fields = (tpl.spec_fields || [])
      .filter((f) => (f.label || "").trim())
      .map((f) => {
        let key = (f.key || "").trim() || toKey(f.label, seen);
        if (seen.has(key)) key = toKey(key, seen);
        seen.add(key);
        return {
          key,
          label: f.label.trim(),
          type: f.type || "text",
          options: f.type === "select"
            ? (f.options || []).filter((o) => o && o.trim()).map((o) => o.trim())
            : null,
          suffix: (f.suffix || "").trim() || null,
        };
      });
    const checklist = (tpl.checklist || []).map((c) => (c || "").trim()).filter(Boolean);

    const body = {
      name: tpl.name.trim(),
      task_type: tpl.task_type,
      photos_required: !!tpl.photos_required,
      spec_fields,
      checklist,
    };

    setSaving(true);
    try {
      if (tpl.id) {
        const { data } = await api.patch(`/qa/templates/${tpl.id}`, body);
        toast.success("Template saved");
        onSaved(data);
      } else {
        const { data } = await api.post("/qa/templates", body);
        toast.success(`Created "${data.name}"`);
        onSaved(data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-5 space-y-5" data-testid="qa-tpl-editor">
      {isSystem && (
        <div className="border-l-4 border-zinc-800 bg-zinc-100 p-3 text-xs" data-testid="qa-tpl-readonly-notice">
          <b>Shipped template — read-only.</b> Fieldlink keeps these updated. Hit <span className="font-mono">Clone</span> if you want a copy to modify.
        </div>
      )}

      {/* --- Header fields --- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest">Template name *</Label>
          <Input
            value={tpl.name}
            onChange={(e) => setTpl({ ...tpl, name: e.target.value })}
            disabled={isSystem}
            placeholder="e.g. Board & batten fence install"
            className="mt-1.5 rounded-sm border-zinc-400"
            data-testid="qa-tpl-name"
          />
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-widest">Task type</Label>
          <Select
            value={tpl.task_type}
            onValueChange={(v) => setTpl({ ...tpl, task_type: v })}
            disabled={isSystem}
          >
            <SelectTrigger className="mt-1.5 rounded-sm border-zinc-400" data-testid="qa-tpl-tasktype">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {meta.task_types.map((k) => (
                <SelectItem key={k} value={k}>{meta.task_labels[k] || k}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center gap-3 p-3 border border-zinc-200 rounded-sm">
        <Switch
          checked={!!tpl.photos_required}
          onCheckedChange={(v) => setTpl({ ...tpl, photos_required: v })}
          disabled={isSystem}
          data-testid="qa-tpl-photos-required"
        />
        <div className="flex-1">
          <div className="text-sm font-bold">Photos required</div>
          <div className="text-[11px] text-zinc-500">Field workers can't submit this QA without at least one photo attached.</div>
        </div>
      </div>

      {/* --- Spec fields --- */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="flex items-center gap-2">
              <Ruler className="w-4 h-4 text-[#FF5F15]" />
              <span className="font-display font-black uppercase tracking-tight text-sm">Spec fields</span>
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">Measurements, materials or setup details specific to this task. Field workers fill these in on the phone.</div>
          </div>
          {!isSystem && (
            <Button variant="outline" size="sm" onClick={addSpec} className="rounded-sm uppercase tracking-wider font-bold text-xs" data-testid="qa-tpl-add-spec">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add field
            </Button>
          )}
        </div>

        {tpl.spec_fields.length === 0 ? (
          <div className="border border-dashed border-zinc-300 p-4 text-center text-[11px] text-zinc-400 italic">
            No spec fields — the crew will just see the checklist and photos.
          </div>
        ) : (
          <div className="space-y-2">
            {tpl.spec_fields.map((f, i) => (
              <div key={i} className="border border-zinc-200 rounded-sm p-2.5 space-y-2 bg-zinc-50" data-testid={`qa-tpl-spec-${i}`}>
                <div className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-12 md:col-span-4">
                    <Label className="text-[9px] font-bold uppercase tracking-widest">Label</Label>
                    <Input
                      value={f.label}
                      onChange={(e) => {
                        const label = e.target.value;
                        // Auto-fill the key from label until the user types their own
                        const wasAutoKeyed = !f.key || f.key === toKey(f.label, new Set());
                        const newKey = wasAutoKeyed ? toKey(label, existingKeys) : f.key;
                        updSpec(i, { label, key: newKey });
                      }}
                      disabled={isSystem}
                      placeholder="e.g. Concrete strength"
                      className="mt-1 rounded-sm border-zinc-400 text-sm"
                      data-testid={`qa-tpl-spec-label-${i}`}
                    />
                  </div>
                  <div className="col-span-6 md:col-span-3">
                    <Label className="text-[9px] font-bold uppercase tracking-widest">Type</Label>
                    <Select
                      value={f.type || "text"}
                      onValueChange={(v) => updSpec(i, { type: v, options: v === "select" ? (f.options || [""]) : null })}
                      disabled={isSystem}
                    >
                      <SelectTrigger className="mt-1 rounded-sm border-zinc-400 text-sm" data-testid={`qa-tpl-spec-type-${i}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.keys(FIELD_TYPE_LABEL).map((k) => (
                          <SelectItem key={k} value={k}>{FIELD_TYPE_LABEL[k]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-6 md:col-span-4">
                    <Label className="text-[9px] font-bold uppercase tracking-widest">Suffix / unit</Label>
                    <Input
                      value={f.suffix || ""}
                      onChange={(e) => updSpec(i, { suffix: e.target.value })}
                      disabled={isSystem}
                      placeholder="MPa · mm · m³ · %"
                      className="mt-1 rounded-sm border-zinc-400 text-sm font-mono"
                      data-testid={`qa-tpl-spec-suffix-${i}`}
                    />
                  </div>
                  <div className="col-span-12 md:col-span-1 flex md:flex-col gap-1 md:justify-end">
                    {!isSystem && (
                      <>
                        <button onClick={() => moveSpec(i, -1)} disabled={i === 0} className="text-zinc-400 hover:text-zinc-700 disabled:opacity-30 p-1 md:p-0" title="Move up">
                          <GripVertical className="w-4 h-4 rotate-180" />
                        </button>
                        <button onClick={() => moveSpec(i, 1)} disabled={i === tpl.spec_fields.length - 1} className="text-zinc-400 hover:text-zinc-700 disabled:opacity-30 p-1 md:p-0" title="Move down">
                          <GripVertical className="w-4 h-4" />
                        </button>
                        <button onClick={() => rmSpec(i)} className="text-zinc-400 hover:text-red-600 p-1 md:p-0" title="Remove" data-testid={`qa-tpl-spec-rm-${i}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Options — only for select type */}
                {f.type === "select" && (
                  <div className="pl-2 border-l-2 border-[#FF5F15]/40">
                    <Label className="text-[9px] font-bold uppercase tracking-widest">Choices</Label>
                    <div className="space-y-1 mt-1">
                      {(f.options || []).map((opt, oi) => (
                        <div key={oi} className="flex gap-1">
                          <Input
                            value={opt}
                            onChange={(e) => {
                              const nxt = [...(f.options || [])];
                              nxt[oi] = e.target.value;
                              updSpec(i, { options: nxt });
                            }}
                            disabled={isSystem}
                            placeholder={`Option ${oi + 1}`}
                            className="rounded-sm border-zinc-400 text-sm"
                            data-testid={`qa-tpl-spec-opt-${i}-${oi}`}
                          />
                          {!isSystem && (
                            <button
                              onClick={() => {
                                const nxt = (f.options || []).filter((_, idx) => idx !== oi);
                                updSpec(i, { options: nxt.length ? nxt : [""] });
                              }}
                              className="text-zinc-400 hover:text-red-600 px-2"
                              title="Remove option"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      ))}
                      {!isSystem && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => updSpec(i, { options: [...(f.options || []), ""] })}
                          className="rounded-sm h-6 text-[10px] font-bold uppercase tracking-widest"
                          data-testid={`qa-tpl-spec-add-opt-${i}`}
                        >
                          <Plus className="w-3 h-3 mr-1" /> Add option
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* --- Checklist --- */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="flex items-center gap-2">
              <ListChecks className="w-4 h-4 text-[#FF5F15]" />
              <span className="font-display font-black uppercase tracking-tight text-sm">Checklist</span>
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">Each item gets a Pass / Fail / N/A tick on the phone.</div>
          </div>
          {!isSystem && (
            <Button variant="outline" size="sm" onClick={addChecklist} className="rounded-sm uppercase tracking-wider font-bold text-xs" data-testid="qa-tpl-add-check">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add item
            </Button>
          )}
        </div>

        {tpl.checklist.length === 0 ? (
          <div className="border border-dashed border-zinc-300 p-4 text-center text-[11px] text-zinc-400 italic">
            No checklist items yet.
          </div>
        ) : (
          <div className="space-y-1.5">
            {tpl.checklist.map((c, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`qa-tpl-check-${i}`}>
                <div className="w-6 text-center text-[10px] font-mono text-zinc-400">{i + 1}</div>
                <Input
                  value={c}
                  onChange={(e) => updChecklist(i, e.target.value)}
                  disabled={isSystem}
                  placeholder="e.g. Post spacings within 2m"
                  className="rounded-sm border-zinc-400 text-sm"
                  data-testid={`qa-tpl-check-input-${i}`}
                />
                {!isSystem && (
                  <>
                    <button onClick={() => moveChecklist(i, -1)} disabled={i === 0} className="text-zinc-400 hover:text-zinc-700 disabled:opacity-30" title="Move up">
                      <GripVertical className="w-4 h-4 rotate-180" />
                    </button>
                    <button onClick={() => moveChecklist(i, 1)} disabled={i === tpl.checklist.length - 1} className="text-zinc-400 hover:text-zinc-700 disabled:opacity-30" title="Move down">
                      <GripVertical className="w-4 h-4" />
                    </button>
                    <button onClick={() => rmChecklist(i)} className="text-zinc-400 hover:text-red-600" title="Remove" data-testid={`qa-tpl-check-rm-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* --- Actions --- */}
      <DialogFooter className="p-0 pt-2 border-t border-zinc-200 flex-row gap-2">
        <Button variant="outline" onClick={onCancel} className="rounded-sm uppercase tracking-wider font-bold text-xs" data-testid="qa-tpl-cancel">
          Cancel
        </Button>
        {!isSystem && (
          <Button
            onClick={save}
            disabled={saving || !tpl.name.trim()}
            className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs"
            data-testid="qa-tpl-save"
          >
            <Save className="w-3.5 h-3.5 mr-1.5" />
            {saving ? "Saving…" : (tpl.id ? "Save changes" : "Create template")}
          </Button>
        )}
      </DialogFooter>
    </div>
  );
}
